'use client';

/**
 * BFF calls for the account & workspace surfaces.
 */

import type { ApiClient } from '@/lib/api-client';
import type { WorkspaceItem } from './types';
import { normalizeKind } from './types';

interface WorkspacesResponse {
  results: Array<{ clerk_id: string; name: string; kind: string }>;
}

export async function fetchWorkspaces(api: ApiClient): Promise<WorkspaceItem[]> {
  const data = await api.get<WorkspacesResponse>('/api/v1/organizations/mine/');
  return (data.results ?? []).map((row) => ({
    clerk_id: row.clerk_id,
    name: row.name,
    kind: normalizeKind(row.kind),
  }));
}

export interface RenameResult {
  id: string;
  django_id: number;
  name: string;
  slug: string;
  kind: string;
}

export function renameWorkspace(api: ApiClient, name: string): Promise<RenameResult> {
  return api.patch<RenameResult>('/api/v1/organizations/', { name });
}

export interface InviteResult {
  invitation_id: string;
  will_become_team: boolean;
}

export function inviteToWorkspace(
  api: ApiClient,
  payload: { email: string; team_name?: string }
): Promise<InviteResult> {
  return api.post<InviteResult>('/api/v1/organizations/invitations/', payload);
}

export function leaveWorkspace(api: ApiClient): Promise<{ status: string }> {
  return api.post<{ status: string }>('/api/v1/organizations/leave/', {});
}

export interface ProfileResult {
  full_name: string;
  first_name: string;
  last_name: string;
  clerk_synced: boolean;
}

export function updateProfile(api: ApiClient, fullName: string): Promise<ProfileResult> {
  return api.patch<ProfileResult>('/api/v1/auth/profile/', { full_name: fullName });
}

export interface RevokeSessionsResult {
  revoked: number;
  failed: number;
}

export function revokeAllSessions(api: ApiClient): Promise<RevokeSessionsResult> {
  return api.post<RevokeSessionsResult>('/api/v1/auth/sessions/revoke-all/', {});
}

export interface ProvisionResult {
  clerk_organization_id: string;
  organization_id: number;
  client_id: number | null;
  workspace_name: string;
  kind: string;
  company_domain: string;
}

export function provisionPersonalWorkspace(
  api: ApiClient,
  payload: {
    workspace_name: string;
    idempotency_key: string;
    accepted_terms: boolean;
    turnstile_token?: string;
    challenge_stamp?: string;
    full_name?: string;
    company_domain?: string;
  }
): Promise<ProvisionResult> {
  return api.post<ProvisionResult>('/api/v1/onboarding/personal-workspace/', payload);
}

export interface ChallengeStampResult {
  challenge_stamp: string;
}

/**
 * Public (pre-signup) challenge mint: a single-use stamp bound to the
 * signup email. No bot token. Unauthenticated by design — no session
 * exists yet — so it uses raw fetch, never the JWT client.
 */
export async function requestOnboardingChallenge(
  email: string
): Promise<ChallengeStampResult> {
  const base = process.env.NEXT_PUBLIC_API_URL || '';
  const response = await fetch(`${base}/api/v1/onboarding/challenge/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { detail?: string };
      if (typeof body?.detail === 'string' && body.detail.trim()) {
        detail = body.detail;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(detail || 'Something went wrong. Please try again.');
  }
  return (await response.json()) as ChallengeStampResult;
}

/**
 * Re-attach to an existing personal workspace.
 *
 * Clerk membership can be missing while the BFF still owns the workspace
 * (purge + re-signup, deleted Clerk membership). The backend repairs the
 * Clerk membership idempotently and returns the org to activate. 404
 * (no_workspace) means Create is genuinely correct.
 */
export function recoverWorkspace(api: ApiClient): Promise<ProvisionResult> {
  return api.post<ProvisionResult>('/api/v1/organizations/recover/', {});
}
